import { useCallback, useState } from 'react';
import {HiMiniIdentification, HiOutlineEye} from "react-icons/hi2";
import {MdOutlineCancel, MdOutlineDownload, MdOutlineModeEditOutline} from "react-icons/md";
import { Link } from 'react-router-dom';
import { ActionIcon, Button, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import { FormDrawerView } from "../../../components/forms/FormDrawer";
import { useFiltersPR } from '../../../components/providers/FiltersProvider';
import { FilterDrawerView } from '../../../components/table/components/TableFilter';
import CustomTable from '../../../components/table/CustomTable';
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import Page from '../../../layout/Page/Page';
import { menuRoutes } from '../../../router/menu';
import {ClientsApiResponse} from "../../../type/entities/client-type";
import {InvoiceService} from "../../../services/invoices/invoiceService";
import InvoicesFilters from "./InvoicesFilters";
import InvoiceForm from "../components/InvoiceForm";
import CancelPopup from "../../../components/buttons/CancelPopover";
import DeletePopup from "../../../components/buttons/DeletePopover";
import {IoDocumentTextSharp, IoTimeOutline} from "react-icons/io5";
import {HiOutlineCheckCircle, HiOutlineDownload} from "react-icons/hi";
import {FaCircleInfo} from "react-icons/fa6";
import PaidPopover from "../../../components/buttons/PaidPopover";
import { FaFileExcel } from 'react-icons/fa';
import moment from 'moment';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import cn from '../../../utils/classNames';

export const getInvoiceVisual = (row: any) => {
    if (row?.isCancel) {
        return {
            state: 'cancelled' as const,
            date: row?.cancelDate?.date ?? row?.cancelDate ?? null,
            rowClass: 'row-cancelled',
        };
    }
    if (row?.isPaid) {
        return {
            state: 'paid' as const,
            date: row?.paymentDate?.date ?? row?.paymentDate ?? null,
            rowClass: 'row-paid',
        };
    }
    return {
        state: 'pending' as const,
        date: null,
        rowClass: 'row-pending',
    };
};

export const invoiceStatuses = [
    { label: 'Pagada', value: 'paid', color: '#10b981', gradientBg: 'bg-gradient-to-r from-green-100 to-white/25 to-80%' },              // Verde llamativo
    { label: 'Cancelada', value: 'cancelled', color: '#ef4444', gradientBg: 'bg-gradient-to-r from-red-100 to-white/25 to-80%' },                       // Rojo intenso
];

const columnOrder = [
    'id',
    'serialCode',
    'company',
    'client',
    'clientCifNif',
    'address',
    'invoiceDate',
    'kgQuantity',
    'totalAmount',
    'ivaTax',
    'unitPrice',
    'isCancel',
    'cancelDate',
    'statusDate',
    'cancelReason',
    'isPaid',
    'paymentDate',
    'createdAt',
    'updatedAt',
    'comments'
];

interface InvoicesListProps {
    openFormDrawer: boolean;
    setOpenFormDrawer: React.Dispatch<React.SetStateAction<boolean>>;
    invoiceSelected: any;
    setInvoiceSelected: React.Dispatch<React.SetStateAction<any>>;
    type?: 'proforma' | 'invoice';
}

const InvoicesList: React.FC<InvoicesListProps> = ({ openFormDrawer, setOpenFormDrawer, invoiceSelected, setInvoiceSelected, type = 'proforma' }) => {

    // STATES

    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [exporting, setExporting] = useState<boolean>(false);

    const service = new InvoiceService();


    // HOOKS

    const { userCan } = usePrivilege();
    const { handleErrors } = useHandleErrors();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();

    const isProforma = type === 'proforma';
    const docName = isProforma ? 'proforma' : 'factura';
    const DocName = isProforma ? 'Proforma' : 'Factura';

    // FETCH DATA

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES Obtiene el listado de facturas/proformas
     * @EN Gets the list of invoices/proformas
     */
    // -------------------------------------------------------------------------------------------------------------
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const filtersWithType = {
            ...filters,
            filter_filters: {
                ...filters.filter_filters,
                is_paid: !isProforma
            }
        };
        const response = await service.listInvoices(filtersWithType);
        return response.getResponseData() as ClientsApiResponse[];
    }, [filters, isProforma]));
    // -------------------------------------------------------------------------------------------------------------


    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES Maneja la cancelación de una o multiples facturas
     * @EN Handles the cancellation of one or multiple invoices
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleCancel = async (ids: string[], reason: string | undefined) => {
            try {

                const response = await (await service.cancelInvoice(ids, reason)).getResponseData();
                if (response.success) {
                    refetch();
                    toast.success('Proforma/s cancelada/s correctamente');
                } else {
                    handleErrors(response);
                }
            } catch (error) {
                handleErrors(error);
            }
        };
    //-------------------------------------------------------------------------------------------------------------


    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES Maneja el pago de una o multiples facturas
     *
     * @EN Handles the payment of one or multiple invoices
     */
    //-------------------------------------------------------------------------------------------------------------
    const handlePaid = async (ids: string[]) => {
            try {

                const response = await (await service.paidInvoice(ids)).getResponseData();
                if (response.success) {
                    refetch();
                    toast.success('Proforma/s pagada/s correctamente');
                } else {
                    handleErrors(response);
                }
            } catch (error) {
                handleErrors(error);
            }
        };
    //-------------------------------------------------------------------------------------------------------------


    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES Maneja la eliminación de múltiples facturas
     * @EN Handles the deletion of multiple invoices
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleMultiDelete = async (ids: string[]) => {
        try {

            const response = await (await service.deleteMultiInvoices(ids)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Proforma eliminadas correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------


    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES Obtiene el estado de la factura
     * @EN Gets the invoice status
     *
     * @param row
     */
    // -------------------------------------------------------------------------------------------------------------
    const getInvoiceStatus = (row: any) => {
        if (row?.isCancel) {
            return {
                label: "Cancelada",
                colorClass: "bg-rose-100 text-rose-700 border border-rose-200",
                Icon: MdOutlineCancel,
                tooltip:
                    row?.cancelDate
                        ? `Cancelada el ${new Date(row.cancelDate?.date).toLocaleString()}${row?.cancelReason ? ` · ${row.cancelReason}` : ''}`
                        : (row?.cancelReason || "Cancelada"),
            };
        }
        if (row?.isPaid) {
            return {
                label: "Pagada",
                colorClass: "bg-emerald-100 text-emerald-700 border border-emerald-200",
                Icon: HiOutlineCheckCircle,
                tooltip:
                    row?.paymentDate
                        ? `Pagada el ${new Date(row.paymentDate?.date).toLocaleString()}`
                        : "Pagada",
            };
        }
        return {
            label: "Pendiente",
            colorClass: "bg-amber-100 text-amber-700 border border-amber-200",
            Icon: IoTimeOutline,
            tooltip: "Pendiente de pago",
        };
    };
    // ------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES EXPORTA RECOGIDAS A EXCEL
     * @EN EXPORTS PICKUPS TO EXCEL
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleExportExcel = async () => {
        try {
            setExporting(true);
            const response = await service.exportInvoicesToExcel(filters);
            const fileData = response.getResponseData();
            const blob = new Blob([fileData], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `proformas_clientes_${moment().format('DD-MM-YYYY_HH-mm-ss')}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success('Archivo Excel descargado con éxito');
        } catch (error: any) {
            toast.error(error.message || 'Error al exportar a Excel');
            setExporting(false);
        } finally {
            setExporting(false);
        }
    };
    //-------------------------------------------------------------------------------------------------------------


    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES EXPORTA RECOGIDAS A PDF
     * @EN EXPORTS PICKUPS TO PDF
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleMultiExportPdf = async (selectedRows: any[]) => {
      try {
            // Extraer los IDs de las filas seleccionadas
            const ids = selectedRows.map(row => row.id);
            
            const response = await service.exportMultiInvoicesToPDF(ids);
          if (response && response.getResponseData()) {
              const fileData = response.getResponseData();
              const contentType = response.response?.headers?.['content-type'] || '';

              if (contentType.includes('application/pdf')) {
                  if (fileData) {
                      const blob = new Blob([fileData], { type: 'application/pdf' });
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      
                      // Generar nombre personalizado para una sola factura
                      let fileName = `proforma_cliente_${moment().format('DD-MM-YYYY_HH-mm-ss')}.pdf`;
                      
                      if (selectedRows.length === 1) {
                          const invoice = selectedRows[0];
                          const clientName = invoice.client?.companyName || 'Cliente';
                          const serialCode = invoice.serialCode || 'SIN_CODIGO';
                          
                          // Limpiar caracteres especiales del nombre
                          const cleanClientName = clientName.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
                          const cleanSerialCode = serialCode.replace(/[^a-zA-Z0-9\-]/g, '');
                          
                          fileName = `${cleanClientName}_${cleanSerialCode}.pdf`;
                      }
                      
                      link.download = fileName;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      window.URL.revokeObjectURL(url);
                      toast.success('Proforma descargada con éxito');
                  }
              }
              else if (contentType.includes('application/zip')) {
                  if (fileData) {
                      const blob = new Blob([fileData], { type: 'application/zip' });
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                    
                      // Generar nombre del archivo ZIP con información de las facturas
                      const timestamp = moment().format('DD-MM-YYYY_HH-mm-ss');
                      let fileName = `proformas_clientes_${timestamp}.zip`;
                      
                      if (selectedRows.length > 1) {
                          // Para múltiples facturas, usar el número de facturas en el nombre
                          fileName = `proformas_${selectedRows.length}_clientes_${timestamp}.zip`;
                      }
                      
                      link.setAttribute('download', fileName);
                    
                      // Descargar el archivo
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      window.URL.revokeObjectURL(url);
                    
                      toast.success('Proformas descargadas correctamente en formato ZIP');
                  } else {
                      toast.error('No se pudieron obtener los archivos de proformas');
                  }
              }
              else {
                  toast.error('Error al descargar las proformas');
              }
          }
        } catch (error: any) {
            toast.error(`Error al descargar las proformas: ${error.message || 'Error desconocido'}`);
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MUESTRA LOS DATOS EN EL ORDEN CORRECTO
     * @EN SHOWS THE DATA IN THE CORRECT ORDER
     */
    //-------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data.map((row: any) => {
        const visual = getInvoiceVisual(row);
        const orderedRow: any = {};

        columnOrder.forEach((key) => {
            if (key === 'statusDate') {
                orderedRow.statusDate = visual.date; // guardamos raw
            } else {
                orderedRow[key] = row[key];
            }
        });

        // opcional: guarda el estado para el estilo de fila si tu CustomTable lo usa
        orderedRow.__rowState = visual.state;
        return orderedRow;
    });
    //-------------------------------------------------------------------------------------------------------------

    // RENDER

    return (
        <Page container='fluid'>
            {data !== undefined && (
                <CustomTable
                    id={'invoices-table'}
                    columnOrder={columnOrder}
                    data={orderedData()}
                    floatingHorizontalScrollbar
                    isLoading={loading}
                    isSortable
                    classNameTable='fs-table'
                    headerTableClassName='fs-table-header'
                    customRowsBackgroundColors={
                        invoiceStatuses.map((status) => ({
                            column: '__rowState',
                            values: [status.value],
                            gradientBg: status.gradientBg,
                            useGradient: true
                        }))
                    }
                    columnsNoSortables={[]}
                    columnsNotShown={['cancelDate','paymentDate','isCancel','isPaid', '__rowState', 'cancelReason', 'clientCifNif', 'address', 'company', 'comments']}
                    overrideColumns={[
                        {
                            key: 'serialCode',
                            render: (row: any) => {
                                const original = row?.row?.original ?? row;
                                return (
                                    <span className="inline-flex items-center gap-2">
                                        <IoDocumentTextSharp className="h-4 w-4 shrink-0 text-gray-700" />
                                        <a
                                            href={`/invoices/${original.id}/profile`}
                                            className="font-bold hover:text-primary hover:underline"
                                        >
                                          {original.serialCode}
                                        </a>
                                    </span>
                                );
                            },
                        },
                        {
                            key: 'statusDate',
                            customHeader: 'Fecha de estado', // si tu CustomTable permite custom header
                            render: (row: any) => {
                                const original = row?.row?.original ?? row;
                                const { state, date } = getInvoiceVisual(original);
                                const label =
                                    state === 'cancelled' ? 'Cancelada' :
                                        state === 'paid' ? 'Pagada' : '—';

                                const dt = date ? new Date(date) : null;
                                const formatted = dt
                                    ? dt.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' })
                                    : '—';

                                return (
                                    <div className="flex flex-col">
                                        <span className="text-[11px] text-gray-500">{label}</span>
                                        <span>{formatted}</span>

                                        {state === 'cancelled' && date && (
                                            <Tooltip
                                                content={original?.cancelReason ? `${original.cancelReason}` : 'Sin motivo especificado'}
                                                placement="top"
                                                color="invert"
                                            >
                                                <span className="mt-1 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
                                                  <FaCircleInfo className="h-3.5 w-3.5" />
                                                  Motivo
                                                </span>
                                            </Tooltip>
                                        )}
                                    </div>
                                );
                            },
                        },
                        {
                            key: 'client',
                            customHeader: 'Cliente',
                            render: (row: any) => {
                                const original = row?.row?.original ?? row;
                                const c = original?.client ?? {};

                                const companyName = c?.companyName ?? '—';
                                const cif = original?.clientCifNif ?? c?.cifNif ?? '—';
                                const addr = c?.address ?? original?.address ?? '—';
                                const clientId = c?.id;

                                const nameNode = clientId ? (
                                    <Link
                                        to={`/clients/${clientId}/profile/info`}
                                        className="inline-flex items-center gap-1 font-semibold hover:underline hover:text-primary whitespace-nowrap"
                                    >
                                        <HiMiniIdentification className="h-4 w-4 shrink-0" />
                                        <span className="truncate">{companyName}</span>
                                    </Link>
                                ) : (
                                    <span className="font-semibold">{companyName}</span>
                                );

                                return (
                                    <div className="flex flex-col">
                                        {nameNode}
                                        <div className="mt-0.5 text-xs text-gray-600">
                                            <div className="leading-tight">
                                                <span className="uppercase tracking-wide text-[10px] text-gray-400 mr-1">CIF/NIF</span>
                                                {cif}
                                            </div>
                                            <div className="leading-tight truncate">
                                                <span className="uppercase tracking-wide text-[10px] text-gray-400 mr-1">Dirección</span>
                                                {addr}
                                            </div>
                                        </div>
                                    </div>
                                );
                            },
                        },
                    ]}
                    actions={[
                        {
                            label: 'View',
                            permissions: { group: 'invoice', permission: 'get' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={`Ver ${docName}`}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Link
                                            to={`${menuRoutes.invoices.path}/${row.id}/profile/${menuRoutes.invoices.profile.info}`}>
                                            <ActionIcon
                                                as="span"
                                                size="sm"
                                                variant="outline"
                                                className="hover:!border-gray-900 hover:text-gray-700 bg-white w-6 h-6"
                                            >
                                                <HiOutlineEye className="h-4 w-4" size={20} />
                                            </ActionIcon>
                                        </Link>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Edit',
                            permissions: { group: 'invoice', permission: 'edit' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={`Editar ${docName}`}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            onClick={() => {
                                                setInvoiceSelected(row);
                                                setOpenFormDrawer(true);
                                            }}
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white w-6 h-6"
                                        >
                                            <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Paid',
                            permissions: { group: 'invoice', permission: 'paid' },
                            hide: (row: any) => {
                                // Accede de forma segura a la fila original
                                const original = row?.row?.original ?? row;
                                // Solo mostrar si NO está pagada y NO está cancelada
                                return Boolean(original?.isPaid) || Boolean(original?.isCancel);
                            },
                            render: (row: any) => {

                                return (
                                    <Tooltip size="sm" content={`Marcar ${docName} como pagada`} placement="top" color="invert">
                                        <div>
                                            <PaidPopover
                                                title={`Pagar ${docName}`}
                                                description={`¿Estás seguro de que deseas pagar la ${docName} ${row.serialCode}?`}
                                                onDelete={() => handlePaid([row.id])}
                                                actionIconClassName="w-6 h-6"
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Cancel',
                            permissions: { group: 'invoice', permission: 'cancel' },
                            hide: (row: any) => {
                                // Accede de forma segura a la fila original
                                const original = row?.row?.original ?? row;
                                // Solo mostrar si NO está pagada y NO está cancelada
                                return Boolean(original?.isPaid) || Boolean(original?.isCancel);
                            },
                            render: (row: any) => {
                                return (
                                    <Tooltip size="sm" content={`Cancelar ${docName}`} placement="top" color="invert">
                                        <div>
                                            <CancelPopup
                                                title={`Cancelar ${docName}`}
                                                description={`¿Estás seguro de que deseas cancelar la ${docName} ${row.serialCode}?`}
                                                onDelete={(reason) => handleCancel([row.id], reason)}
                                                actionIconClassName="w-6 h-6"
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Delete',
                            permissions: { group: 'invoice', permission: 'delete' },
                            render: (row: any) => {
                                return (
                                    <Tooltip size="sm" content={'Eliminar proforma'} placement="top" color="invert">
                                        <div>
                                            <DeletePopup
                                                title="Eliminar proforma"
                                                description={`¿Estás seguro de que deseas eliminar la proforma ${row.serialCode}?`}
                                                onDelete={() => handleMultiDelete([row.id])}
                                                actionIconClassName="w-6 h-6"
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Export',
                            permissions: { group: 'invoice', permission: 'export' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Exportar proforma'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            onClick={() => {
                                                handleMultiExportPdf([row]);
                                            }}
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white w-6 h-6"
                                        >
                                            <MdOutlineDownload className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        }, 
                        // {
                        //     label: 'Delete',
                        //     permissions: { group: 'invoice', permission: 'delete' },
                        //     render: (row: any) => {
                        //         return (
                        //             <Tooltip
                        //                 size="sm"
                        //                 content={'Eliminar factura'}
                        //                 placement="top"
                        //                 color="invert"
                        //             >
                        //                 <div>
                        //                     <DeletePopover
                        //                         title={`Eliminar factura`}
                        //                         description={`¿Estás seguro de que deseas eliminar la factura ${row.serialCode}?`}
                        //                         onDelete={() => handleDelete(row.id)}
                        //                     />
                        //                 </div>
                        //             </Tooltip>
                        //         );
                        //     },
                        // },
                    ]}
                    handleMultipleDelete={handleMultiDelete}
                    multiActions={[
                        {
                            label: `Descargar ${isProforma ? 'proformas' : 'facturas'} seleccionadas`,
                            permissions: {
                                group: 'invoice',
                                permission: 'export'
                            },
                            render: (ids: any[], selectedRows: any[], resetSelection: () => void) => {
                                return (
                                    <Tooltip
                                        size="md"
                                        content={`Exportar ${isProforma ? 'proformas' : 'facturas'} seleccionadas`}
                                        placement="top"
                                    >
                                        <ActionIcon
                                            size="lg"
                                            color="primary"
                                            variant="outline"
                                            aria-label={`Exportar ${isProforma ? 'proformas' : 'facturas'}`}
                                            className={cn(['cursor-pointer hover:!border-orange-600 hover:text-orange-600 border-orange-500 text-orange-500'])}
                                            onClick={() => {
                                                handleMultiExportPdf(selectedRows);
                                            }}
                                        >
                                            <HiOutlineDownload className="h-6 w-6" />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as ClientsApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                >
                    {userCan('export', 'invoice') && (
                        <Button
                            variant="solid"
                            size="sm"
                            onClick={() => handleExportExcel()}
                            className="p-4 bg-green-600"
                        >
                            <FaFileExcel className="mr-2 min-h-[20px] min-w-[20px]" />
                            Exportar Excel
                        </Button>
                    )}
                </CustomTable>
            )}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros de facturas'} onClear={() => resetFilters(50)}>
                <InvoicesFilters filters={filters} updateFilters={updateFilters} type={type} />
            </FilterDrawerView>

            <FormDrawerView
                isOpen={openFormDrawer}
                setOpenDrawer={setOpenFormDrawer}
                subtitleText={
                    invoiceSelected
                        ? `Completa el formulario para editar los datos de la ${docName}`
                        : `Completa el formulario para emitir una nueva ${docName} a un cliente`
                }
                drawerTitle={invoiceSelected ? `Editar ${DocName}` : `Emitir ${DocName}`}
                submitText={invoiceSelected ? `Guardar ${DocName}` : `Emitir ${DocName}`}
            >
                <InvoiceForm
                    setOpenModal={setOpenFormDrawer}
                    invoiceSelected={invoiceSelected}
                    refetch={refetch}
                    isPaid={!isProforma}
                />
            </FormDrawerView>
        </Page>
    );
};

export default InvoicesList;